import { createFileRoute } from "@tanstack/react-router";

import { InstagramPageContent } from "@/components/site/Instagram";
import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { getInstagramFeed } from "@/lib/instagram.functions";
import { INSTAGRAM_URL } from "@/lib/instagram";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { safeLoader } from "@/lib/safe-loader";

const TITLE = "Instagram – Swedish International School en Estepona";
const DESCRIPTION =
  "Sigue la vida escolar en Estepona y Marbella: fotos y reels de las clases, el deporte, las excursiones y los eventos del colegio sueco de la Costa del Sol.";

export const Route = createFileRoute("/es/instagram")({
  loader: () => safeLoader("es.instagram", () => getInstagramFeed(), { posts: [], highlights: [] }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/es/instagram") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/instagram"),
  }),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      El contenido no se ha podido cargar ahora mismo. Inténtalo de nuevo en un momento.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      Página no encontrada.
    </div>
  ),
  component: InstagramPageEs,
});

function InstagramPageEs() {
  const { posts, highlights } = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Instagram"
        title="La vida escolar en imágenes"
        intro="En Instagram compartimos lo que ocurre durante la jornada escolar: clases, deporte, excursiones y eventos. Aquí recogemos el contenido para que puedas seguirnos sin tener cuenta."
      />

      <InstagramPageContent posts={posts} highlights={highlights} locale="es" />

      <Prose>
        <h2>Por qué compartimos nuestro día a día</h2>
        <p>
          Muchas familias nos encuentran mucho antes de mudarse a España. Las imágenes de la jornada
          escolar dan una impresión honesta de cómo es esto: grupos reducidos, currículo sueco y un
          patio que el alumnado puede usar casi todo el año. Solo publicamos cosas que han ocurrido
          realmente en el colegio y siempre pedimos permiso a las familias antes de que un alumno
          aparezca en una foto.
        </p>
        <h2>Síguenos</h2>
        <p>
          La cuenta se actualiza con regularidad durante el curso.{" "}
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
            Sigue a @swedish_international_school
          </a>{" "}
          para ver reels del deporte, fotos de las aulas e información antes de cada inicio de
          periodo.
        </p>
      </Prose>

      <CtaBand locale="es" />
    </>
  );
}
